import React from 'react';
import { T, tintFor, initialsOf } from './login.theme';
import { COPY, notAWaiterMessage } from './login.copy';
import { AlertIcon, CheckIcon } from './LoginIcons';
import { Dialog, Field, Banner, PrimaryButton, GhostButton } from './LoginControls';
import { authenticate, isWaiter, businessIdOf, authErrorMessage } from './authenticate';
import { addProfile } from './waiterProfiles';
import { writeTillBusinessId } from './tillStore';

/**
 * Pin an existing waiter to this till.
 *
 * This does NOT create an account — the API only lets a manager do that, and
 * this screen has no token. The waiter already exists; typing their password
 * here proves it is really them and tells us the server's opinion of their
 * role. Then the password is dropped and only their name and username stay.
 *
 * The check calls the same /users/authenticate as a real sign-in, so it comes
 * back with a working token. THAT TOKEN IS THROWN AWAY. Verifying somebody
 * must not sign them in — the person at the keyboard adding a waiter is not
 * the waiter.
 */
export default function AddWaiterDialog({ onClose, onAdded }) {
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [refusal, setRefusal] = React.useState(null);
  const [added, setAdded] = React.useState(null);

  const reset = () => {
    setUsername('');
    setPassword('');
    setError('');
    setRefusal(null);
    setAdded(null);
  };

  const submit = async () => {
    if (busy) return;
    if (!username.trim() || !password) {
      setError(COPY.required);
      return;
    }

    setBusy(true);
    setError('');
    setRefusal(null);

    try {
      const { user } = await authenticate({ username: username.trim(), password });

      if (!isWaiter(user)) {
        // Clear the password but keep the username on screen — whoever typed
        // it needs to see which account was refused.
        setPassword('');
        setRefusal(notAWaiterMessage(user?.role));
        return;
      }

      const businessId = businessIdOf(user);
      if (!businessId) {
        setError('That account has no business attached. Ask a manager to check it.');
        return;
      }

      // The till now knows which business it serves, which is what scopes the
      // tiles on every later boot.
      writeTillBusinessId(businessId);

      const waiterId = user._id || user.id;
      const profiles = addProfile(businessId, {
        id: waiterId,
        name: user.name || user.username,
        username: user.username,
      });

      // Show the tile with the colour it was actually given, not a guess.
      setAdded(profiles.find((profile) => profile.id === waiterId));
      onAdded(profiles);
    } catch (err) {
      setPassword('');
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (added) {
    const tint = tintFor(added);
    return (
      <Dialog onClose={onClose}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 20,
            padding: '34px 32px 28px',
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 52,
              height: 52,
              borderRadius: T.radiusPill,
              background: T.paidTint,
              color: T.paid,
            }}
          >
            <CheckIcon size={24} />
          </span>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: -0.2, color: T.ink }}>
              {COPY.addedTitle(added.name)}
            </span>
            <span
              style={{
                fontSize: 12.5,
                color: T.inkSoft,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              {COPY.addedBody}
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              width: 190,
              padding: 14,
              background: T.selectedBg,
              border: `1px solid ${T.selectedBorder}`,
              borderRadius: T.radiusPanel,
              boxSizing: 'border-box',
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 44,
                borderRadius: T.radiusPill,
                background: tint.bg,
                fontSize: 15,
                fontWeight: 700,
                color: tint.ink,
              }}
            >
              {initialsOf(added.name)}
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 13.5, fontWeight: 600, color: T.ink }}>{added.name}</span>
              <span style={{ fontSize: 11.5, color: T.inkFaint }}>{added.username}</span>
            </span>
          </div>

          <div style={{ display: 'flex', gap: 10, width: '100%' }}>
            <GhostButton style={{ flex: 1 }} onClick={reset}>
              {COPY.addAnother}
            </GhostButton>
            <PrimaryButton style={{ flex: 1 }} onClick={onClose}>
              {COPY.done}
            </PrimaryButton>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog onClose={onClose} locked={busy}>
      <div style={{ display: 'flex', flexDirection: 'column', padding: '22px 24px 22px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, paddingRight: 34 }}>
          <span style={{ fontSize: 16.5, fontWeight: 700, letterSpacing: -0.1, color: T.ink }}>
            {COPY.addTitle}
          </span>
          <span style={{ fontSize: 12.5, color: T.inkSoft, lineHeight: 1.5 }}>{COPY.addSub}</span>
        </div>

        {refusal ? (
          <div style={{ marginTop: 18 }}>
            <Banner tone="danger" icon={<AlertIcon size={16} />} title={refusal.title}>
              {refusal.body}
            </Banner>
          </div>
        ) : null}

        {error ? (
          <div style={{ marginTop: 18 }}>
            <Banner tone="danger" icon={<AlertIcon size={16} />}>
              {error}
            </Banner>
          </div>
        ) : null}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 18 }}>
          <Field
            label={COPY.username}
            name="add-waiter-username"
            value={username}
            onChange={(value) => {
              setUsername(value);
              setRefusal(null);
              setError('');
            }}
            onEnter={submit}
            disabled={busy}
            invalid={Boolean(refusal)}
            autoFocus
          />
          <Field
            label={COPY.password}
            name="add-waiter-password"
            type="password"
            value={password}
            onChange={(value) => {
              setPassword(value);
              setError('');
            }}
            onEnter={submit}
            disabled={busy}
          />
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
          <GhostButton style={{ flex: 1 }} onClick={onClose} disabled={busy}>
            {COPY.cancel}
          </GhostButton>
          <PrimaryButton style={{ flex: 1.4 }} onClick={submit} busy={busy}>
            {busy ? COPY.checking : COPY.checkAndAdd}
          </PrimaryButton>
        </div>

        <span style={{ fontSize: 11.5, color: T.inkFaint, lineHeight: 1.5, marginTop: 18 }}>
          {COPY.addNote}
        </span>
      </div>
    </Dialog>
  );
}
